import React, { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import { 
  Box, 
  Typography, 
  TextField, 
  Button, 
  Grid, 
  Tabs, 
  Tab,
  Paper,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  Alert
} from '@mui/material';
import { message } from 'antd';
import DatePicker from 'react-datepicker';
import moment from 'moment';
import 'react-datepicker/dist/react-datepicker.css';
import { getListEmailByAddress, sendEmailForVoting } from '../../../../../redux/actions/daoUI/emailReminder.action';

// TabPanel component for tab content
function TabPanel({ children, value, index, ...other }) {
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`voting-tabpanel-${index}`}
      aria-labelledby={`voting-tab-${index}`}
      {...other}
    >
      {value === index && (
        <Box sx={{ p: 3 }}>
          {children}
        </Box>
      )}
    </div>
  );
}

const VotingTab = ({ chainId, currentDao, daoOrganizerAddress, getListEmailByAddress, sendEmailForVoting }) => {
  const [email, setEmail] = useState('');
  const [tabValue, setTabValue] = useState(0);
  const [startTime, setStartTime] = useState(moment());
  const [endTime, setEndTime] = useState(moment().add(1, 'hour'));
  const [csvFile, setCsvFile] = useState(null);
  const [membersList, setMembersList] = useState([]);
  const [importError, setImportError] = useState('');
  const [isOrganizer, setIsOrganizer] = useState(false);
  const url = `https://www.xdao.app/${chainId}/dao/${currentDao.gtTokenAddress}`;

  useEffect(() => {
    const checkOrganizerPermission = () => {
      if (window.ethereum && window.ethereum.selectedAddress && daoOrganizerAddress) {
        const currentAddress = window.ethereum.selectedAddress.toLowerCase();
        const organizerAddress = daoOrganizerAddress.toLowerCase();
        setIsOrganizer(currentAddress === organizerAddress);
      } else {
        setIsOrganizer(false);
      }
    };

    checkOrganizerPermission();

    // Listen for account changes
    if (window.ethereum) {
      const handleAccountsChanged = () => {
        checkOrganizerPermission();
      };

      window.ethereum.on('accountsChanged', handleAccountsChanged);

      return () => {
        window.ethereum.removeListener('accountsChanged', handleAccountsChanged);
      };
    }
  }, [daoOrganizerAddress]);
  
  const handleTabChange = (event, newValue) => {
    setTabValue(newValue);
  };
  
  const handleSubmit = (e) => {
    e.preventDefault();
    // Xử lý logic gửi email reminder ở đây
    console.log('Email for reminders:', email);
    message.success('Email reminder subscribed successfully!');
    setEmail('');
  };

  const handleFileChange = (event) => {
    const file = event.target.files[0];
    if (file && file.type === 'text/csv') {
      setCsvFile(file);
      setImportError('');
    } else {
      setCsvFile(null);
      setImportError('Please select a valid CSV file');
    }
  };

  const handleImportCsv = async () => {
    if (!csvFile) {
      setImportError('Please select a CSV file');
      return;
    }

    try {
      setImportError('');
      
      // Dispatch the Redux action to submit the CSV file
      const response = await getListEmailByAddress(csvFile);
      console.log('response', response)
      // Handle the response and set the members list
      if (response && response.data?.length > 0) {
        setMembersList(response.data);
        console.log('Imported members from API:', response.data);
      } else {
        setMembersList([]);
        setImportError('No email data found for the provided addresses');
      }
    } catch (error) {
      setImportError('Error processing CSV file: ' + (error.message || 'Unknown error'));
      console.error('CSV import error:', error);
    }
  };

  const handleSendEmails = async () => {
    if (!startTime || !endTime) {
      message.warning('Please select start and end time');
      return;
    }
    
    if (membersList.length === 0) {
      message.warning('Please import member list first');
      return;
    }

    if (!csvFile) {
      message.warning('CSV file is required for sending emails');
      return;
    }

    try {
      // Prepare the email data with time parameters
      const emailData = {
        csvFile,
        startTime: startTime.format('YYYY-MM-DD HH:mm:ss'),
        endTime: endTime.format('YYYY-MM-DD HH:mm:ss'),
        membersList,
        daoId: currentDao._id
      };

      console.log('Sending emails with data:', emailData);
      
      // Call the Redux action to send emails
      const response = await sendEmailForVoting(emailData);
      
      console.log('Email sending response:', response);
      
      // Show the actual response message
      if (response && response.message) {
        message.success(response.message);
      } else {
        message.success(`Email reminder sent successfully to ${membersList.length} DAO members!`);
      }
      
      // Reset the data after successful sending
      setMembersList([]);
      setCsvFile(null);
      setImportError('');
      // Reset file input
      const fileInput = document.querySelector('input[type="file"]');
      if (fileInput) {
        fileInput.value = '';
      }
      
    } catch (error) {
      console.error('Error sending emails:', error);
      message.error('Failed to send email reminders. Please try again.');
    }
  };
  
  return (
    <Box sx={{ p: 4 }}>
      {/* Horizontal Tabs */}
      <Box sx={{ borderBottom: 1, borderColor: 'rgba(255, 255, 255, 0.1)', mb: 3 }}>
        <Tabs 
          value={tabValue} 
          onChange={handleTabChange} 
          aria-label="voting tabs"
          sx={{
            '& .MuiTabs-indicator': {
              backgroundColor: '#00EDC3',
            },
            '& .MuiTab-root': {
              color: 'rgba(255, 255, 255, 0.7)',
              '&.Mui-selected': {
                color: '#00EDC3',
              },
            },
          }}
        >
          <Tab label="Voting" />
          {isOrganizer && <Tab label="Email Reminder" />}
        </Tabs>
      </Box>

      {/* Voting Tab Content */}
      <TabPanel value={tabValue} index={0}>
        <iframe
          src={url}
          width="100%"
          height="600"
          style={{ border: 'none', borderRadius: 8 }}
          title="XDAO Voting"
          allowFullScreen
        />
      </TabPanel>

      {/* Email Reminder Tab Content */}
      {isOrganizer && (
        <TabPanel value={tabValue} index={1}>
        <Grid container spacing={3}>
          {/* Time Selection */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Email Reminder Time
            </Typography>
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                    Start time
                  </Typography>
                  <DatePicker
                    selected={startTime}
                    onChange={(date) => setStartTime(date)}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="MM/DD/YYYY"
                    customInput={
                      <TextField
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.2)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#00EDC3',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#fff',
                          },
                        }}
                      />
                    }
                  />
                </Box>
              </Grid>
              <Grid item xs={12} md={6}>
                <Box>
                  <Typography sx={{ mb: 1, color: 'rgba(255, 255, 255, 0.7)' }}>
                    End time
                  </Typography>
                  <DatePicker
                    selected={endTime}
                    onChange={(date) => setEndTime(date)}
                    showTimeSelect
                    timeFormat="HH:mm"
                    timeIntervals={15}
                    dateFormat="MM/DD/YYYY"
                    customInput={
                      <TextField
                        fullWidth
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.2)',
                            },
                            '&:hover fieldset': {
                              borderColor: 'rgba(255, 255, 255, 0.3)',
                            },
                            '&.Mui-focused fieldset': {
                              borderColor: '#00EDC3',
                            },
                          },
                          '& .MuiInputBase-input': {
                            color: '#fff',
                          },
                        }}
                      />
                    }
                  />
                </Box>
              </Grid>
            </Grid>
          </Grid>

          {/* CSV Import Section */}
          <Grid item xs={12}>
            <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
              Import DAO Council Members
            </Typography>
            <Paper sx={{ p: 3, backgroundColor: 'rgba(255, 255, 255, 0.05)' }}>
              <Grid container spacing={2} alignItems="center">
                <Grid item xs={12} md={8}>
                  <TextField
                    type="file"
                    fullWidth
                    inputProps={{ accept: '.csv' }}
                    onChange={handleFileChange}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.2)',
                        },
                        '&:hover fieldset': {
                          borderColor: 'rgba(255, 255, 255, 0.3)',
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: '#00EDC3',
                        },
                      },
                      '& .MuiInputBase-input': {
                        color: '#fff',
                      },
                    }}
                  />
                </Grid>
                <Grid item xs={12} md={4}>
                  <Button
                    variant="outlined"
                    fullWidth
                    onClick={handleImportCsv}
                    disabled={!csvFile}
                    sx={{
                      borderColor: '#00EDC3',
                      color: '#00EDC3',
                      '&:hover': {
                        borderColor: '#00c9a3',
                        backgroundColor: 'rgba(0, 237, 195, 0.1)',
                      },
                    }}
                  >
                    Import
                  </Button>
                </Grid>
              </Grid>
              
              {importError && (
                <Alert severity="error" sx={{ mt: 2 }}>
                  {importError}
                </Alert>
              )}
            </Paper>
          </Grid>

          {/* Members Preview */}
          {membersList.length > 0 && (
            <Grid item xs={12}>
              <Typography variant="h6" sx={{ mb: 2, color: '#fff' }}>
                Member List ({membersList.length})
              </Typography>
              <Paper sx={{ 
                backgroundColor: 'rgba(255, 255, 255, 0.05)',
                maxHeight: 300,
                overflow: 'auto'
              }}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell sx={{ 
                        color: '#fff', 
                        fontWeight: 'bold',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        Address
                      </TableCell>
                      <TableCell sx={{ 
                        color: '#fff', 
                        fontWeight: 'bold',
                        borderBottom: '1px solid rgba(255, 255, 255, 0.2)'
                      }}>
                        Email
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {membersList.map((member, index) => (
                      <TableRow key={index} sx={{
                        '&:hover': {
                          backgroundColor: 'rgba(255, 255, 255, 0.05)'
                        }
                      }}>
                        <TableCell sx={{ 
                          color: '#fff',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          {member.address || 'Unknown'}
                        </TableCell>
                        <TableCell sx={{ 
                          color: 'rgba(255, 255, 255, 0.7)',
                          borderBottom: '1px solid rgba(255, 255, 255, 0.1)'
                        }}>
                          {member.email || 'No email'}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Paper>
            </Grid>
          )}

          {/* Send Email Button */}
          <Grid item xs={12}>
            <Box sx={{ textAlign: 'center', mt: 3 }}>
              <Button
                variant="contained"
                size="large"
                onClick={handleSendEmails}
                disabled={!startTime || !endTime || membersList.length === 0}
                sx={{
                  backgroundColor: '#00EDC3',
                  color: '#282f38',
                  px: 4,
                  py: 1.5,
                  '&:hover': {
                    backgroundColor: '#00c9a3',
                  },
                  '&:disabled': {
                    backgroundColor: 'rgba(255, 255, 255, 0.1)',
                    color: 'rgba(255, 255, 255, 0.3)',
                  },
                }}
              >
                Send Email Reminder
              </Button>
            </Box>
          </Grid>
        </Grid>
      </TabPanel>
      )}
    </Box>
  );
};

export default connect(null, { getListEmailByAddress, sendEmailForVoting })(VotingTab);
